import { useState, useEffect, useRef } from "react";
import api from "@/lib/api";
import { formatApiError } from "@/utils/formatters";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Gear, FloppyDisk, Warning, PaintBrush, Eye, UploadSimple, Tag, Trash, Plus, Database, TrashSimple, EyeSlash, DownloadSimple, ArrowCounterClockwise, Palette, Code, ClipboardText, ChartBar } from "@phosphor-icons/react";
import { useThemeContext, THEME_OPTIONS } from "@/contexts/ThemeContext";
import { useAuth } from "@/contexts/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function SettingField({ label, description, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-[0.15em] text-[#A3A3A3]">{label}</Label>
      {children}
      {description && <p className="text-xs text-[#737373]">{description}</p>}
    </div>
  );
}

function FileUploadField({ label, description, currentUrl, onUploaded, testId }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post("/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const fullUrl = `${API_URL}${data.url}`;
      onUploaded(fullUrl);
      toast.success("File uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <SettingField label={label} description={description}>
      <div className="flex items-center gap-2">
        <Input
          value={currentUrl || ""}
          onChange={(e) => onUploaded(e.target.value)}
          placeholder="URL or upload a file"
          className="bg-[#0A0A0A] border-[#262626] text-white rounded-sm placeholder:text-[#737373] flex-1 text-xs"
          data-testid={`${testId}-url`}
        />
        <input ref={fileRef} type="file" accept="image/*,.ico" className="hidden" onChange={handleUpload} data-testid={`${testId}-file`} />
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={uploading}
          onClick={() => fileRef.current?.click()}
          className="border-[#262626] text-[#A3A3A3] hover:bg-[#1A1A1A] hover:text-white rounded-sm shrink-0"
          data-testid={`${testId}-upload-btn`}
        >
          <UploadSimple size={14} className="mr-1" />
          {uploading ? "..." : "Upload"}
        </Button>
      </div>
      {currentUrl && (
        <div className="mt-1.5 flex items-center gap-2">
          <img src={currentUrl} alt="Preview" className="h-8 w-8 object-contain rounded-sm border border-[#262626] bg-[#0A0A0A]" />
          <span className="text-[10px] text-[#737373] truncate max-w-[200px]">{currentUrl}</span>
        </div>
      )}
    </SettingField>
  );
}

const FONT_OPTIONS = [
  "Barlow Condensed", "DM Sans", "JetBrains Mono", "Oswald", "Rajdhani",
  "Chakra Petch", "Orbitron", "Exo 2", "Saira", "Russo One",
  "Play", "Audiowide", "Teko", "Roboto Condensed", "Titillium Web",
  "Inter", "Poppins", "Montserrat", "Lato", "Raleway",
  "Open Sans", "Nunito", "Playfair Display", "Source Code Pro", "Space Grotesk",
  "Manrope", "Outfit", "Sora", "Work Sans", "Urbanist",
];

function RecordManagementCard() {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);

  const handlePreview = async () => {
    if (!startDate || !endDate) return;
    try {
      const s = new Date(startDate).toISOString();
      const e = new Date(endDate + "T23:59:59").toISOString();
      const { data } = await api.get(`/validations/manage/preview?start_date=${s}&end_date=${e}`);
      setPreview(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const handleAction = async (action) => {
    if (!startDate || !endDate) return;
    setConfirmAction(action);
  };

  const executeAction = async () => {
    if (!confirmAction || !startDate || !endDate) return;
    setLoading(true);
    try {
      const s = new Date(startDate).toISOString();
      const e = new Date(endDate + "T23:59:59").toISOString();
      const endpoint = confirmAction === "unhide" ? "/validations/unhide" : "/validations/manage";
      const { data } = await api.post(endpoint, { start_date: s, end_date: e, action: confirmAction });
      toast.success(data.message);
      setPreview(null);
      setConfirmAction(null);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const VERBS = { delete: "permanently delete", hide: "hide", unhide: "unhide" };
  const COLORS = { delete: "#FF3B30", hide: "#FF9F0A", unhide: "#007AFF" };

  return (
    <Card className="bg-[#121212] border-[#262626] rounded-sm shadow-none">
      <CardHeader className="pb-3">
        <CardTitle className="font-heading text-sm font-semibold text-white uppercase tracking-wider flex items-center gap-2">
          <Database size={16} weight="bold" className="text-[#FF3B30]" />
          Record Management
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-[#737373]">Delete or hide validation records within a custom date range. Hidden records are excluded from all views but can be restored.</p>
        <div className="grid grid-cols-2 gap-4">
          <SettingField label="Start Date">
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
              className="bg-[#0A0A0A] border-[#262626] text-white rounded-sm font-mono-data" data-testid="manage-start-date" />
          </SettingField>
          <SettingField label="End Date">
            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
              className="bg-[#0A0A0A] border-[#262626] text-white rounded-sm font-mono-data" data-testid="manage-end-date" />
          </SettingField>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handlePreview}
            className="border-[#262626] text-[#A3A3A3] hover:bg-[#1A1A1A] hover:text-white rounded-sm text-xs" data-testid="manage-preview-btn">
            <Eye size={14} className="mr-1" /> Preview
          </Button>
          {preview && (
            <span className="text-xs text-[#A3A3A3] font-mono-data">
              {preview.total} records ({preview.visible} visible, {preview.hidden} hidden)
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 pt-2 border-t border-[#262626]">
          <Button size="sm" disabled={loading || !startDate || !endDate} onClick={() => handleAction("hide")}
            className="bg-[#FF9F0A] hover:bg-[#FF9F0A]/80 text-black rounded-sm text-xs" data-testid="manage-hide-btn">
            <EyeSlash size={14} className="mr-1" /> Hide Records
          </Button>
          <Button size="sm" disabled={loading || !startDate || !endDate} onClick={() => handleAction("unhide")}
            variant="outline" className="border-[#262626] text-[#A3A3A3] hover:bg-[#1A1A1A] hover:text-white rounded-sm text-xs" data-testid="manage-unhide-btn">
            <Eye size={14} className="mr-1" /> Unhide Records
          </Button>
          <Button size="sm" disabled={loading || !startDate || !endDate} onClick={() => handleAction("delete")}
            className="bg-[#FF3B30] hover:bg-[#FF3B30]/80 text-white rounded-sm text-xs" data-testid="manage-delete-btn">
            <TrashSimple size={14} className="mr-1" /> Delete Permanently
          </Button>
        </div>
        {confirmAction && (
          <div className="bg-[#FF3B30]/10 border border-[#FF3B30]/30 rounded-sm p-4 space-y-3">
            <p className="text-sm font-semibold" style={{ color: COLORS[confirmAction] }}>
              Are you sure you want to {VERBS[confirmAction]} records?
            </p>
            <p className="text-xs text-[#A3A3A3]">
              Date range: <span className="text-white font-mono-data">{startDate}</span> to <span className="text-white font-mono-data">{endDate}</span>
              {preview && <span> ({preview.total} records)</span>}
            </p>
            <div className="flex items-center gap-2">
              <Button size="sm" onClick={executeAction} disabled={loading}
                className="text-white rounded-sm text-xs" style={{ backgroundColor: COLORS[confirmAction] }}
                data-testid="manage-confirm-btn">
                {loading ? "Processing..." : `Confirm ${confirmAction}`}
              </Button>
              <Button size="sm" variant="outline" onClick={() => setConfirmAction(null)}
                className="border-[#262626] text-[#A3A3A3] hover:bg-[#1A1A1A] hover:text-white rounded-sm text-xs">
                Cancel
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function BackupRestoreCard() {
  const fileRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [restoring, setRestoring] = useState(false);

  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await api.get("/backup/export", { responseType: "blob" });
      const blob = new Blob([response.data]);
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `backup_${new Date().toISOString().replace(/[:.]/g, "").slice(0, 15)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
      toast.success("Backup exported");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Backup export failed");
    } finally {
      setExporting(false);
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post("/backup/import", formData, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported: ${data.validations_imported} validations, ${data.car_profiles_imported} profiles${data.settings_restored ? ", settings restored" : ""}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleRestore = async () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".json";
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      if (!window.confirm("WARNING: Full restore will WIPE all existing validations and car profiles and replace them with the backup. Are you sure?")) return;
      setRestoring(true);
      try {
        const formData = new FormData();
        formData.append("file", file);
        const { data } = await api.post("/backup/restore", formData, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success(`Restored: ${data.validations_restored} validations, ${data.car_profiles_restored} profiles${data.settings_restored ? ", settings restored" : ""}`);
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail));
      } finally {
        setRestoring(false);
      }
    };
    input.click();
  };

  return (
    <Card className="bg-[#121212] border-[#262626] rounded-sm shadow-none">
      <CardHeader className="pb-3">
        <CardTitle className="font-heading text-sm font-semibold text-white uppercase tracking-wider flex items-center gap-2">
          <Database size={16} weight="bold" className="text-[#007AFF]" />
          Backup & Restore
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-[#737373]">Export all system data (validations, users, car profiles, settings) or restore from a previous backup.</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Button onClick={handleExport} disabled={exporting}
            className="bg-[#007AFF] hover:bg-[#3395FF] text-white rounded-sm text-xs h-10" data-testid="backup-export-btn">
            <DownloadSimple size={16} className="mr-1.5" /> {exporting ? "Exporting..." : "Export Backup"}
          </Button>
          <div>
            <input ref={fileRef} type="file" accept=".json" className="hidden" onChange={handleImport} data-testid="backup-import-file" />
            <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={importing}
              className="w-full border-[#262626] text-[#A3A3A3] hover:bg-[#1A1A1A] hover:text-white rounded-sm text-xs h-10" data-testid="backup-import-btn">
              <UploadSimple size={16} className="mr-1.5" /> {importing ? "Importing..." : "Import (Merge)"}
            </Button>
          </div>
          <Button variant="outline" onClick={handleRestore} disabled={restoring}
            className="border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#FF3B30]/10 hover:text-[#FF3B30] rounded-sm text-xs h-10" data-testid="backup-restore-btn">
            <ArrowCounterClockwise size={16} className="mr-1.5" /> {restoring ? "Restoring..." : "Full Restore"}
          </Button>
        </div>
        <div className="flex items-start gap-2 bg-[#FF9F0A]/10 border border-[#FF9F0A]/30 rounded-sm p-3 text-xs text-[#FF9F0A]">
          <Warning size={14} weight="bold" className="mt-0.5 shrink-0" />
          <div>
            <strong>Export</strong> downloads a JSON file with all data. <strong>Import (Merge)</strong> adds new records without deleting existing ones. <strong>Full Restore</strong> wipes existing data and replaces it entirely.
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ClearAuditLogCard() {
  const [startDate, setStartDate] = useState("");
  const [startTime, setStartTime] = useState("00:00");
  const [endDate, setEndDate] = useState("");
  const [endTime, setEndTime] = useState("23:59");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const buildIso = (date, time) => {
    if (!date) return "";
    return new Date(`${date}T${time || "00:00"}:00`).toISOString();
  };

  const handlePreview = async () => {
    if (!startDate || !endDate) return;
    try {
      const s = buildIso(startDate, startTime);
      const e = buildIso(endDate, endTime);
      const { data } = await api.get(`/audit-logs/clear/preview?start_date=${s}&end_date=${e}`);
      setPreview(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const handleClear = async () => {
    if (!startDate || !endDate) return;
    setLoading(true);
    try {
      const s = buildIso(startDate, startTime);
      const e = buildIso(endDate, endTime);
      const { data } = await api.post("/audit-logs/clear", { start_date: s, end_date: e });
      toast.success(data.message);
      setPreview(null);
      setConfirmOpen(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="bg-[#121212] border-[#262626] rounded-sm shadow-none">
      <CardHeader className="pb-3">
        <CardTitle className="font-heading text-sm font-semibold text-white uppercase tracking-wider flex items-center gap-2">
          <ClipboardText size={16} weight="bold" className="text-[#FF3B30]" />
          Clear Audit Log
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-[#737373]">Permanently delete audit log entries within a custom date and time range. This action cannot be undone.</p>
        <div className="grid grid-cols-2 gap-4">
          <SettingField label="Start Date">
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
              className="bg-[#0A0A0A] border-[#262626] text-white rounded-sm font-mono-data" data-testid="audit-clear-start-date" />
          </SettingField>
          <SettingField label="Start Time">
            <Input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)}
              className="bg-[#0A0A0A] border-[#262626] text-white rounded-sm font-mono-data" data-testid="audit-clear-start-time" />
          </SettingField>
          <SettingField label="End Date">
            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
              className="bg-[#0A0A0A] border-[#262626] text-white rounded-sm font-mono-data" data-testid="audit-clear-end-date" />
          </SettingField>
          <SettingField label="End Time">
            <Input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)}
              className="bg-[#0A0A0A] border-[#262626] text-white rounded-sm font-mono-data" data-testid="audit-clear-end-time" />
          </SettingField>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handlePreview} disabled={!startDate || !endDate}
            className="border-[#262626] text-[#A3A3A3] hover:bg-[#1A1A1A] hover:text-white rounded-sm text-xs" data-testid="audit-clear-preview-btn">
            <Eye size={14} className="mr-1" /> Preview
          </Button>
          {preview && (
            <span className="text-xs font-mono-data text-[#A3A3A3]">
              {preview.total} audit log(s) will be deleted
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 pt-2 border-t border-[#262626]">
          <Button size="sm" disabled={loading || !startDate || !endDate}
            onClick={() => setConfirmOpen(true)}
            className="bg-[#FF3B30] hover:bg-[#FF3B30]/80 text-white rounded-sm text-xs" data-testid="audit-clear-delete-btn">
            <TrashSimple size={14} className="mr-1" /> Clear Audit Logs
          </Button>
        </div>
        {confirmOpen && (
          <div className="bg-[#FF3B30]/10 border border-[#FF3B30]/30 rounded-sm p-4 space-y-3">
            <p className="text-sm text-[#FF3B30] font-semibold">Are you sure?</p>
            <p className="text-xs text-[#A3A3A3]">
              This will permanently delete {preview?.total ?? "all matching"} audit log entries from{" "}
              <span className="text-white font-mono-data">{startDate} {startTime}</span> to{" "}
              <span className="text-white font-mono-data">{endDate} {endTime}</span>. This cannot be undone.
            </p>
            <div className="flex items-center gap-2">
              <Button size="sm" onClick={handleClear} disabled={loading}
                className="bg-[#FF3B30] hover:bg-[#FF3B30]/80 text-white rounded-sm text-xs" data-testid="audit-clear-confirm-btn">
                <TrashSimple size={14} className="mr-1" /> {loading ? "Deleting..." : "Confirm Delete"}
              </Button>
              <Button size="sm" variant="outline" onClick={() => setConfirmOpen(false)}
                className="border-[#262626] text-[#A3A3A3] hover:bg-[#1A1A1A] hover:text-white rounded-sm text-xs">
                Cancel
              </Button>
            </div>
          </div>
        )}
        <div className="flex items-start gap-2 bg-[#FF9F0A]/10 border border-[#FF9F0A]/30 rounded-sm p-3 text-xs text-[#FF9F0A]">
          <Warning size={14} weight="bold" className="mt-0.5 shrink-0" />
          <span>Only superadmin can clear audit logs. This action is itself logged for accountability.</span>
        </div>
      </CardContent>
    </Card>
  );
}

function ClearAnalyticsCard() {
  const [startDate, setStartDate] = useState("");
  const [startTime, setStartTime] = useState("00:00");
  const [endDate, setEndDate] = useState("");
  const [endTime, setEndTime] = useState("23:59");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const buildIso = (date, time) => {
    if (!date) return "";
    return new Date(`${date}T${time || "00:00"}:00`).toISOString();
  };

  const handlePreview = async () => {
    if (!startDate || !endDate) return;
    try {
      const s = buildIso(startDate, startTime);
      const e = buildIso(endDate, endTime);
      const { data } = await api.get(`/analytics/clear/preview?start_date=${s}&end_date=${e}&clear_car_profiles=true`);
      setPreview(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const handleClear = async () => {
    if (!startDate || !endDate) return;
    setLoading(true);
    try {
      const s = buildIso(startDate, startTime);
      const e = buildIso(endDate, endTime);
      const { data } = await api.post("/analytics/clear", { start_date: s, end_date: e, clear_car_profiles: true });
      toast.success(data.message);
      setPreview(null);
      setConfirmOpen(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="bg-[#121212] border-[#262626] rounded-sm shadow-none">
      <CardHeader className="pb-3">
        <CardTitle className="font-heading text-sm font-semibold text-white uppercase tracking-wider flex items-center gap-2">
          <ChartBar size={16} weight="bold" className="text-[#FF3B30]" />
          Clear Analytics Data
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-[#737373]">Permanently delete validation records (analytics source data) within a custom date and time range. This action cannot be undone.</p>
        <div className="grid grid-cols-2 gap-4">
          <SettingField label="Start Date">
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
              className="bg-[#0A0A0A] border-[#262626] text-white rounded-sm font-mono-data" data-testid="analytics-clear-start-date" />
          </SettingField>
          <SettingField label="Start Time">
            <Input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)}
              className="bg-[#0A0A0A] border-[#262626] text-white rounded-sm font-mono-data" data-testid="analytics-clear-start-time" />
          </SettingField>
          <SettingField label="End Date">
            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
              className="bg-[#0A0A0A] border-[#262626] text-white rounded-sm font-mono-data" data-testid="analytics-clear-end-date" />
          </SettingField>
          <SettingField label="End Time">
            <Input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)}
              className="bg-[#0A0A0A] border-[#262626] text-white rounded-sm font-mono-data" data-testid="analytics-clear-end-time" />
          </SettingField>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handlePreview} disabled={!startDate || !endDate}
            className="border-[#262626] text-[#A3A3A3] hover:bg-[#1A1A1A] hover:text-white rounded-sm text-xs" data-testid="analytics-clear-preview-btn">
            <Eye size={14} className="mr-1" /> Preview
          </Button>
          {preview && (
            <span className="text-xs font-mono-data text-[#A3A3A3]">
              {preview.total} validation record(s) and {preview.profiles ?? 0} car profile(s) will be deleted
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 pt-2 border-t border-[#262626]">
          <Button size="sm" disabled={loading || !startDate || !endDate}
            onClick={() => setConfirmOpen(true)}
            className="bg-[#FF3B30] hover:bg-[#FF3B30]/80 text-white rounded-sm text-xs" data-testid="analytics-clear-delete-btn">
            <TrashSimple size={14} className="mr-1" /> Clear Analytics Data
          </Button>
        </div>
        {confirmOpen && (
          <div className="bg-[#FF3B30]/10 border border-[#FF3B30]/30 rounded-sm p-4 space-y-3">
            <p className="text-sm text-[#FF3B30] font-semibold">Are you sure?</p>
            <p className="text-xs text-[#A3A3A3]">
              This will permanently delete {preview?.total ?? "all matching"} validation records from{" "}
              <span className="text-white font-mono-data">{startDate} {startTime}</span> to{" "}
              <span className="text-white font-mono-data">{endDate} {endTime}</span>. Car profiles with no remaining validations will also be removed.
            </p>
            <div className="flex items-center gap-2">
              <Button size="sm" onClick={handleClear} disabled={loading}
                className="bg-[#FF3B30] hover:bg-[#FF3B30]/80 text-white rounded-sm text-xs" data-testid="analytics-clear-confirm-btn">
                <TrashSimple size={14} className="mr-1" /> {loading ? "Deleting..." : "Confirm Delete"}
              </Button>
              <Button size="sm" variant="outline" onClick={() => setConfirmOpen(false)}
                className="border-[#262626] text-[#A3A3A3] hover:bg-[#1A1A1A] hover:text-white rounded-sm text-xs">
                Cancel
              </Button>
            </div>
          </div>
        )}
        <div className="flex items-start gap-2 bg-[#FF9F0A]/10 border border-[#FF9F0A]/30 rounded-sm p-3 text-xs text-[#FF9F0A]">
          <Warning size={14} weight="bold" className="mt-0.5 shrink-0" />
          <span>Only superadmin can clear analytics data. This removes validation records which are the source of all analytics.</span>
        </div>
      </CardContent>
    </Card>
  );
}

export function SettingsTab() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const { setTheme, setBranding } = useThemeContext();
  const { user } = useAuth();
  const isSuperadmin = user?.role === "superadmin";

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/settings");
        if (!cancelled) setSettings(data);
      } catch (err) {
        console.error(err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleSave = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      const { data } = await api.put("/settings", settings);
      setSettings(data);
      // Apply theme change live
      if (data.theme) setTheme(data.theme);
      if (data.system_name) document.title = data.system_name;
      // Update branding context so ThemeContext re-applies all custom colors/fonts
      setBranding(data);
      toast.success("Settings saved");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const set = (key, val) => setSettings((prev) => ({ ...prev, [key]: val }));

  if (!settings) return <div className="py-8 text-center text-[#737373]">Loading settings...</div>;

  return (
    <div className="space-y-6 max-w-3xl">
      {/* Room Plate Limit */}
      <Card className="bg-[#121212] border-[#262626] rounded-sm shadow-none">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-sm font-semibold text-white uppercase tracking-wider flex items-center gap-2">
            <Gear size={16} weight="bold" className="text-[#007AFF]" />
            Room Validation Limit
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <SettingField label="Max Car Plates Per Room Per Day" description="Exceeding this triggers a flagged alert.">
            <Input data-testid="setting-max-plates" type="number" min={1} max={100}
              value={settings.max_plates_per_room_per_day}
              onChange={(e) => set("max_plates_per_room_per_day", parseInt(e.target.value) || 1)}
              className="bg-[#0A0A0A] border-[#262626] text-white rounded-sm w-32 font-mono-data" />
          </SettingField>
          <SettingField label="Over-Limit Notification Message" description="Custom pop-up message shown to staff.">
            <textarea data-testid="setting-over-limit-message" value={settings.over_limit_message}
              onChange={(e) => set("over_limit_message", e.target.value)} rows={3}
              className="w-full bg-[#0A0A0A] border border-[#262626] text-white rounded-sm px-3 py-2 text-sm focus:border-[#007AFF] focus:ring-1 focus:ring-[#007AFF] focus:outline-none resize-none" />
          </SettingField>
        </CardContent>
      </Card>

      {/* Timezone */}
      <Card className="bg-[#121212] border-[#262626] rounded-sm shadow-none">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-sm font-semibold text-white uppercase tracking-wider flex items-center gap-2">
            <Gear size={16} weight="bold" className="text-[#007AFF]" />
            Timezone
          </CardTitle>
        </CardHeader>
        <CardContent>
          <SettingField label="System Timezone" description="All timestamps displayed in this timezone.">
            <select data-testid="setting-timezone" value={settings.timezone}
              onChange={(e) => set("timezone", e.target.value)}
              className="w-full bg-[#0A0A0A] border border-[#262626] text-white rounded-sm px-3 py-2 text-sm focus:border-[#007AFF] focus:ring-1 focus:ring-[#007AFF] focus:outline-none">
              {["UTC","US/Eastern","US/Central","US/Mountain","US/Pacific","Europe/London","Europe/Paris","Europe/Berlin","Europe/Moscow","Asia/Dubai","Asia/Kolkata","Asia/Singapore","Asia/Tokyo","Asia/Shanghai","Australia/Sydney","Pacific/Auckland","America/Sao_Paulo","America/Mexico_City","Africa/Cairo","Africa/Johannesburg"].map((tz) => (
                <option key={tz} value={tz}>{tz}</option>
              ))}
            </select>
          </SettingField>
        </CardContent>
      </Card>

      {/* Branding / Customization */}
      <Card className="bg-[#121212] border-[#262626] rounded-sm shadow-none">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-sm font-semibold text-white uppercase tracking-wider flex items-center gap-2">
            <PaintBrush size={16} weight="bold" className="text-[#007AFF]" />
            Login Page & System Branding
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <SettingField label="System Name" description="Displayed on login page header">
              <Input data-testid="setting-system-name" value={settings.system_name || ""}
                onChange={(e) => set("system_name", e.target.value)}
                className="bg-[#0A0A0A] border-[#262626] text-white rounded-sm" />
            </SettingField>
            <SettingField label="System Version" description="Shown below system name">
              <Input data-testid="setting-system-version" value={settings.system_version || ""}
                onChange={(e) => set("system_version", e.target.value)}
                className="bg-[#0A0A0A] border-[#262626] text-white rounded-sm font-mono-data" />
            </SettingField>
          </div>
          <SettingField label="Login Subtitle" description="Text below the system name">
            <Input data-testid="setting-login-subtitle" value={settings.login_subtitle || ""}
              onChange={(e) => set("login_subtitle", e.target.value)}
              className="bg-[#0A0A0A] border-[#262626] text-white rounded-sm" />
          </SettingField>
          <div className="grid grid-cols-2 gap-4">
            <SettingField label="Theme" description="System-wide color theme preset">
              <select data-testid="setting-theme" value={settings.theme || "dark"}
                onChange={(e) => set("theme", e.target.value)}
                className="w-full bg-[#0A0A0A] border border-[#262626] text-white rounded-sm px-3 py-2 text-sm focus:border-[#007AFF] focus:ring-1 focus:ring-[#007AFF] focus:outline-none">
                {THEME_OPTIONS.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </SettingField>
          </div>

          {/* Logo Upload */}
          <FileUploadField
            label="System Logo"
            description="Logo displayed on login page. Upload an image or paste a URL."
            currentUrl={settings.logo_url}
            onUploaded={(url) => set("logo_url", url)}
            testId="setting-logo"
          />

          {/* Favicon Upload */}
          <FileUploadField
            label="System Icon (Favicon)"
            description="Browser tab icon. Upload .ico, .png, or .svg."
            currentUrl={settings.favicon_url}
            onUploaded={(url) => set("favicon_url", url)}
            testId="setting-favicon"
          />

          {/* Login Background */}
          <FileUploadField
            label="Login Background Image"
            description="Full-screen background for login. Upload or paste URL."
            currentUrl={settings.login_bg_url}
            onUploaded={(url) => set("login_bg_url", url)}
            testId="setting-login-bg"
          />

          {/* Login Form Text */}
          <div className="pt-3 border-t border-[#262626]">
            <p className="text-xs uppercase tracking-[0.15em] text-[#A3A3A3] font-semibold mb-3">Login Form Text</p>
            <div className="grid grid-cols-2 gap-4">
              <SettingField label="Email Placeholder" description="Placeholder text in the email field">
                <Input data-testid="setting-email-placeholder" value={settings.login_email_placeholder || ""}
                  onChange={(e) => set("login_email_placeholder", e.target.value)}
                  className="bg-[#0A0A0A] border-[#262626] text-white rounded-sm" />
              </SettingField>
              <SettingField label="Password Placeholder" description="Placeholder text in the password field">
                <Input data-testid="setting-password-placeholder" value={settings.login_password_placeholder || ""}
                  onChange={(e) => set("login_password_placeholder", e.target.value)}
                  className="bg-[#0A0A0A] border-[#262626] text-white rounded-sm" />
              </SettingField>
              <SettingField label="Login Button Text" description="Text on the login button">
                <Input data-testid="setting-button-text" value={settings.login_button_text || ""}
                  onChange={(e) => set("login_button_text", e.target.value)}
                  className="bg-[#0A0A0A] border-[#262626] text-white rounded-sm" />
              </SettingField>
              <SettingField label="Footer Text" description="Text below the login form">
                <Input data-testid="setting-footer-text" value={settings.login_footer_text || ""}
                  onChange={(e) => set("login_footer_text", e.target.value)}
                  className="bg-[#0A0A0A] border-[#262626] text-white rounded-sm" />
              </SettingField>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <SettingField label="Heading Font" description="Font for headings and titles">
              <select data-testid="setting-heading-font" value={settings.heading_font || "Barlow Condensed"}
                onChange={(e) => set("heading_font", e.target.value)}
                className="w-full bg-[#0A0A0A] border border-[#262626] text-white rounded-sm px-3 py-2 text-sm focus:border-[#007AFF] focus:ring-1 focus:ring-[#007AFF] focus:outline-none">
                {FONT_OPTIONS.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
            </SettingField>
            <SettingField label="Body Font" description="Font for body text and forms">
              <select data-testid="setting-body-font" value={settings.body_font || "DM Sans"}
                onChange={(e) => set("body_font", e.target.value)}
                className="w-full bg-[#0A0A0A] border border-[#262626] text-white rounded-sm px-3 py-2 text-sm focus:border-[#007AFF] focus:ring-1 focus:ring-[#007AFF] focus:outline-none">
                {FONT_OPTIONS.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
            </SettingField>
          </div>

          {/* Live Preview */}
          <div className="mt-4">
            <Button variant="outline" size="sm" onClick={() => setShowPreview(!showPreview)} className="border-[#262626] text-[#A3A3A3] hover:bg-[#1A1A1A] hover:text-white rounded-sm text-xs" data-testid="toggle-preview-btn">
              <Eye size={14} className="mr-1.5" /> {showPreview ? "Hide" : "Show"} Preview
            </Button>
            {showPreview && (
              <div className="mt-3 border border-[#262626] rounded-sm p-6 flex flex-col items-center gap-3"
                style={{ backgroundColor: settings.sidebar_color || "#0A0A0A" }}
                data-testid="branding-preview">
                {settings.logo_url ? (
                  <img src={settings.logo_url} alt="Preview" className="h-12 w-auto object-contain" />
                ) : (
                  <div className="w-12 h-12 rounded-sm flex items-center justify-center" style={{ background: `${settings.primary_color || "#007AFF"}15`, border: `1px solid ${settings.primary_color || "#007AFF"}40` }}>
                    <span style={{ color: settings.primary_color || "#007AFF", fontSize: 24 }}>&#9733;</span>
                  </div>
                )}
                <h2 style={{ fontFamily: `'${settings.heading_font || "Barlow Condensed"}', sans-serif`, color: settings.text_color || "#FFFFFF" }} className="text-xl font-bold tracking-tight">{settings.system_name || "SYSTEM NAME"}</h2>
                <p className="text-xs uppercase tracking-[0.2em]" style={{ color: settings.text_color ? `${settings.text_color}99` : "#A3A3A3" }}>{settings.login_subtitle || "Subtitle"}</p>
                {settings.system_version && <p className="text-[10px] font-mono-data" style={{ color: settings.text_color ? `${settings.text_color}66` : "#737373" }}>{settings.system_version}</p>}
                <div className="flex gap-2 mt-1">
                  <div className="h-9 px-6 rounded-sm flex items-center justify-center" style={{ backgroundColor: settings.primary_color || "#007AFF" }}>
                    <p className="text-sm font-medium" style={{ fontFamily: `'${settings.body_font || "DM Sans"}', sans-serif`, color: "#FFF" }}>Primary</p>
                  </div>
                  <div className="h-9 px-6 rounded-sm flex items-center justify-center" style={{ backgroundColor: settings.accent_color || settings.primary_color || "#007AFF" }}>
                    <p className="text-sm font-medium" style={{ fontFamily: `'${settings.body_font || "DM Sans"}', sans-serif`, color: "#FFF" }}>Accent</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Custom Colors */}
      <Card className="bg-[#121212] border-[#262626] rounded-sm shadow-none">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-sm font-semibold text-white uppercase tracking-wider flex items-center gap-2">
            <Palette size={16} weight="bold" className="text-[#007AFF]" />
            Custom Colors
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-xs text-[#737373]">Override theme colors with exact HEX values. Leave blank to use the selected theme preset defaults.</p>
          <div className="grid grid-cols-2 gap-4">
            <SettingField label="Primary Color" description="Buttons, active states, links">
              <div className="flex items-center gap-2">
                <input type="color" value={settings.primary_color || "#007AFF"}
                  onChange={(e) => set("primary_color", e.target.value)}
                  className="w-10 h-9 rounded-sm border border-[#262626] bg-transparent cursor-pointer"
                  data-testid="setting-primary-color" />
                <Input value={settings.primary_color || ""}
                  onChange={(e) => set("primary_color", e.target.value)}
                  placeholder="#007AFF"
                  className="bg-[#0A0A0A] border-[#262626] text-white rounded-sm font-mono-data flex-1" />
              </div>
            </SettingField>
            <SettingField label="Accent Color" description="Badges, highlights, secondary actions">
              <div className="flex items-center gap-2">
                <input type="color" value={settings.accent_color || settings.primary_color || "#007AFF"}
                  onChange={(e) => set("accent_color", e.target.value)}
                  className="w-10 h-9 rounded-sm border border-[#262626] bg-transparent cursor-pointer"
                  data-testid="setting-accent-color" />
                <Input value={settings.accent_color || ""}
                  onChange={(e) => set("accent_color", e.target.value)}
                  placeholder="Same as primary"
                  className="bg-[#0A0A0A] border-[#262626] text-white rounded-sm font-mono-data flex-1" />
              </div>
            </SettingField>
            <SettingField label="Card / Sidebar Color" description="Card backgrounds, sidebar panels">
              <div className="flex items-center gap-2">
                <input type="color" value={settings.sidebar_color || "#121212"}
                  onChange={(e) => set("sidebar_color", e.target.value)}
                  className="w-10 h-9 rounded-sm border border-[#262626] bg-transparent cursor-pointer"
                  data-testid="setting-sidebar-color" />
                <Input value={settings.sidebar_color || ""}
                  onChange={(e) => set("sidebar_color", e.target.value)}
                  placeholder="Theme default"
                  className="bg-[#0A0A0A] border-[#262626] text-white rounded-sm font-mono-data flex-1" />
              </div>
            </SettingField>
            <SettingField label="Text Color" description="Main text and headings">
              <div className="flex items-center gap-2">
                <input type="color" value={settings.text_color || "#FFFFFF"}
                  onChange={(e) => set("text_color", e.target.value)}
                  className="w-10 h-9 rounded-sm border border-[#262626] bg-transparent cursor-pointer"
                  data-testid="setting-text-color" />
                <Input value={settings.text_color || ""}
                  onChange={(e) => set("text_color", e.target.value)}
                  placeholder="Theme default"
                  className="bg-[#0A0A0A] border-[#262626] text-white rounded-sm font-mono-data flex-1" />
              </div>
            </SettingField>
          </div>
          {/* Color swatch preview */}
          <div className="flex items-center gap-2 mt-2">
            {[
              { label: "Primary", color: settings.primary_color },
              { label: "Accent", color: settings.accent_color || settings.primary_color },
              { label: "Card/Sidebar", color: settings.sidebar_color },
              { label: "Text", color: settings.text_color },
            ].filter(c => c.color).map((c) => (
              <div key={c.label} className="flex flex-col items-center gap-1">
                <div className="w-8 h-8 rounded-sm border border-[#262626]" style={{ backgroundColor: c.color }} />
                <span className="text-[9px] text-[#737373] uppercase tracking-wider">{c.label}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Custom CSS */}
      <Card className="bg-[#121212] border-[#262626] rounded-sm shadow-none">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-sm font-semibold text-white uppercase tracking-wider flex items-center gap-2">
            <Code size={16} weight="bold" className="text-[#FF9F0A]" />
            Custom CSS (Advanced)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-[#737373]">Inject custom CSS rules to further customize the UI. Applied globally after all other styles.</p>
          <textarea
            data-testid="setting-custom-css"
            value={settings.custom_css || ""}
            onChange={(e) => set("custom_css", e.target.value)}
            rows={6}
            placeholder={`/* Example: */\n.font-heading { letter-spacing: 0.1em; }\nbutton { border-radius: 8px !important; }`}
            className="w-full bg-[#0A0A0A] border border-[#262626] text-white rounded-sm px-3 py-2 text-xs font-mono-data focus:border-[#007AFF] focus:ring-1 focus:ring-[#007AFF] focus:outline-none resize-y"
          />
          <div className="flex items-start gap-2 bg-[#FF9F0A]/10 border border-[#FF9F0A]/30 rounded-sm p-3 text-xs text-[#FF9F0A]">
            <Warning size={14} weight="bold" className="mt-0.5 shrink-0" />
            <span>Custom CSS is applied globally. Invalid rules may break the layout. Use with care.</span>
          </div>
        </CardContent>
      </Card>

      {/* Custom Risk Levels Editor */}
      <Card className="bg-[#121212] border-[#262626] rounded-sm shadow-none">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-sm font-semibold text-white uppercase tracking-wider flex items-center gap-2">
            <Tag size={16} weight="bold" className="text-[#FF9F0A]" />
            Custom Status Levels (By Number Range)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-xs text-[#737373]">Define custom risk levels with score ranges, labels, and colors. Ranges must cover 0-100 without gaps.</p>
          {(settings.custom_risk_levels || []).map((level, idx) => (
            <div key={level.key || `level-${idx}`} className="flex items-end gap-2 bg-[#0A0A0A] border border-[#262626] rounded-sm p-3" data-testid={`risk-level-${idx}`}>
              <div className="flex-1 space-y-1">
                <Label className="text-[10px] uppercase tracking-wider text-[#737373]">Key</Label>
                <Input value={level.key} onChange={(e) => {
                  const updated = [...settings.custom_risk_levels];
                  updated[idx] = { ...updated[idx], key: e.target.value };
                  set("custom_risk_levels", updated);
                }} className="bg-[#121212] border-[#262626] text-white rounded-sm text-xs h-8" />
              </div>
              <div className="flex-1 space-y-1">
                <Label className="text-[10px] uppercase tracking-wider text-[#737373]">Label</Label>
                <Input value={level.label} onChange={(e) => {
                  const updated = [...settings.custom_risk_levels];
                  updated[idx] = { ...updated[idx], label: e.target.value };
                  set("custom_risk_levels", updated);
                }} className="bg-[#121212] border-[#262626] text-white rounded-sm text-xs h-8" />
              </div>
              <div className="w-16 space-y-1">
                <Label className="text-[10px] uppercase tracking-wider text-[#737373]">Min</Label>
                <Input type="number" min={0} max={100} value={level.min} onChange={(e) => {
                  const updated = [...settings.custom_risk_levels];
                  updated[idx] = { ...updated[idx], min: parseInt(e.target.value) || 0 };
                  set("custom_risk_levels", updated);
                }} className="bg-[#121212] border-[#262626] text-white rounded-sm text-xs h-8 font-mono-data" />
              </div>
              <div className="w-16 space-y-1">
                <Label className="text-[10px] uppercase tracking-wider text-[#737373]">Max</Label>
                <Input type="number" min={0} max={100} value={level.max} onChange={(e) => {
                  const updated = [...settings.custom_risk_levels];
                  updated[idx] = { ...updated[idx], max: parseInt(e.target.value) || 100 };
                  set("custom_risk_levels", updated);
                }} className="bg-[#121212] border-[#262626] text-white rounded-sm text-xs h-8 font-mono-data" />
              </div>
              <div className="w-12 space-y-1">
                <Label className="text-[10px] uppercase tracking-wider text-[#737373]">Color</Label>
                <input type="color" value={level.color} onChange={(e) => {
                  const updated = [...settings.custom_risk_levels];
                  updated[idx] = { ...updated[idx], color: e.target.value };
                  set("custom_risk_levels", updated);
                }} className="w-full h-8 rounded-sm border border-[#262626] bg-transparent cursor-pointer" />
              </div>
              {(settings.custom_risk_levels || []).length > 2 && (
                <Button variant="ghost" size="sm" onClick={() => {
                  const updated = settings.custom_risk_levels.filter((_, i) => i !== idx);
                  set("custom_risk_levels", updated);
                }} className="text-[#FF3B30] hover:text-[#FF3B30] hover:bg-[#FF3B30]/10 h-8 px-2" data-testid={`remove-risk-level-${idx}`}>
                  <Trash size={14} />
                </Button>
              )}
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={() => {
            const levels = settings.custom_risk_levels || [];
            const lastMax = levels.length > 0 ? levels[levels.length - 1].max + 1 : 0;
            set("custom_risk_levels", [...levels, { min: lastMax, max: 100, key: `level_${levels.length}`, label: `Level ${levels.length + 1}`, color: "#007AFF" }]);
          }} className="border-[#262626] text-[#A3A3A3] hover:bg-[#1A1A1A] hover:text-white rounded-sm text-xs" data-testid="add-risk-level-btn">
            <Plus size={14} className="mr-1" /> Add Level
          </Button>
          {/* Visual preview bar */}
          <div className="flex items-center gap-0.5 text-[10px] mt-2">
            {(settings.custom_risk_levels || []).map((lv) => (
              <div key={lv.key || `preview-${lv.min}`} className="flex-1 h-7 rounded-sm flex items-center justify-center border" style={{ background: `${lv.color}20`, borderColor: `${lv.color}60`, color: lv.color }}>
                {lv.label} ({lv.min}-{lv.max})
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Record Management */}
      <RecordManagementCard />

      {/* Backup / Restore */}
      <BackupRestoreCard />

      {/* Clear Audit Log - Superadmin only */}
      {isSuperadmin && <ClearAuditLogCard />}

      {/* Clear Analytics Data - Superadmin only */}
      {isSuperadmin && <ClearAnalyticsCard />}

      {/* Save */}
      <Button onClick={handleSave} disabled={saving}
        className="bg-[#007AFF] hover:bg-[#3395FF] text-white rounded-sm h-10 px-6 text-sm uppercase tracking-wider"
        data-testid="save-settings-btn">
        <FloppyDisk size={16} weight="bold" className="mr-2" />
        {saving ? "Saving..." : "Save All Settings"}
      </Button>
    </div>
  );
}
